import { useEffect, useRef, useState } from 'react';
import {
  endSession,
  markActivity,
  planSessionTick,
  readSessionClocks,
  refreshTokens,
} from '@/lib/authSession';
import { Button } from '@/ui/Button';
import { Modal } from '@/ui/Modal';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];

/**
 * Runs while someone is signed in. Once a second it reads the session's
 * clocks (shared by every tab) and does what planSessionTick says: renew the
 * access token ahead of time for someone active, warn two minutes before the
 * session ends, and sign out when it does.
 *
 * The server enforces all of this regardless; this makes it pleasant -
 * nobody loses a half-filled form to a sign-out they were not told about.
 */
export function SessionKeeper({ onSignOut }) {
  const [plan, setPlan] = useState(null);
  const [absoluteDismissed, setAbsoluteDismissed] = useState(false);
  const refreshing = useRef(false);

  useEffect(() => {
    const onActivity = () => markActivity();
    const onVisible = () => document.visibilityState === 'visible' && markActivity();
    // Opening or reloading the page is activity too - when it is on screen.
    // A tab the browser restores in the background at startup is not someone
    // coming back, and must not keep an idle session alive.
    onVisible();
    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, { passive: true, capture: true }),
    );
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      ACTIVITY_EVENTS.forEach((name) =>
        window.removeEventListener(name, onActivity, { capture: true }),
      );
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    function renew() {
      if (refreshing.current) return;
      refreshing.current = true;
      refreshTokens()
        .catch((error) => {
          // A 401 means the server has already ended it; a network blip does
          // not, and the next tick (or request) tries again.
          if (error?.response?.status === 401) endSession('expired');
        })
        .finally(() => {
          refreshing.current = false;
        });
    }

    function tick() {
      const next = planSessionTick(readSessionClocks());
      if (next.action === 'end-idle') return endSession('idle');
      if (next.action === 'end-absolute') return endSession('absolute');
      if (next.action === 'refresh') renew();
      setPlan(next);
    }

    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, []);

  function staySignedIn() {
    markActivity({ force: true });
    refreshTokens().catch((error) => {
      if (error?.response?.status === 401) endSession('expired');
    });
  }

  const warning = plan?.warning;
  const open = warning === 'idle' || (warning === 'absolute' && !absoluteDismissed);
  const left = formatLeft(plan?.msLeft ?? 0);

  return (
    <Modal
      open={open}
      onClose={warning === 'idle' ? staySignedIn : () => setAbsoluteDismissed(true)}
      title={warning === 'absolute' ? 'Your session is ending' : 'Still there?'}
      size="sm"
    >
      {warning === 'absolute' ? (
        <>
          <p className="text-sm text-content">
            You have been signed in for a long time, so you will be asked to sign in again in{' '}
            <b className="tabular-nums">{left}</b>. Save anything you are working on.
          </p>
          <div className="mt-5 flex justify-end">
            <Button onClick={() => setAbsoluteDismissed(true)}>Got it</Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-content">
            For your security you will be signed out in <b className="tabular-nums">{left}</b>{' '}
            unless you carry on.
          </p>
          <div className="mt-5 flex flex-wrap justify-end gap-2 max-sm:flex-col-reverse">
            <Button variant="secondary" onClick={onSignOut}>
              Sign out
            </Button>
            <Button onClick={staySignedIn}>Stay signed in</Button>
          </div>
        </>
      )}
    </Modal>
  );
}

function formatLeft(ms) {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
